import { State } from '@meraki-internal/state';
import { APIClient } from '@innerhive-internal/innerhive-api-client';
import { BeeChatClient, IChat, IChatMessage } from './BeeChatClient';
import { ICareMapKey } from '../caremap/ICareMap';
import { CareMapState } from '../caremap/CareMapState';
import removeMarkdown from 'remove-markdown';

export const BEA_WELCOME_MESSAGE = `Hi, I'm Bea! I'm here to help with care questions, resources, or just to listen. How can I help?`;

// OpenAI sometimes includes citation markers in responses that we need to strip out.
// Format: \uE200 + "cite" + \uE202 + variable text + \uE201
// Citations can be split across streaming chunks, so we also support stripping partial citations.
const stripCitations = (text: string, stripPartial: boolean): string => {
    // Remove all complete citations (everything between \uE200 and \uE201)
    let result = text.replace(/\uE200[\s\S]*?\uE201/g, '');

    if (stripPartial) {
        // Truncate any partial citation (has \uE200 but no closing \uE201 yet)
        const partialStart = result.lastIndexOf('\uE200');
        if (partialStart !== -1 && result.indexOf('\uE201', partialStart) === -1) {
            result = result.substring(0, partialStart);
        }
    }

    return result;
};

export type BeeChatView = 'chat' | 'list';

interface IBeeChatStateData {
    chats: IChat[];
    activeChat: IChat | null;
    messages: IChatMessage[];
    isSending: boolean;
    isStreaming: boolean;
    streamingText: string;
    currentView: BeeChatView;
}

export class BeeChatState extends State<IBeeChatStateData> {
    static inject = () => [
        BeeChatClient,
        APIClient,
        CareMapState
    ];

    private abortController: AbortController | null = null;

    constructor(
        private chatClient: BeeChatClient,
        private apiClient: APIClient,
        private careMapState: CareMapState
    ) {
        super({
            chats: [],
            activeChat: null,
            messages: [],
            isSending: false,
            isStreaming: false,
            streamingText: '',
            currentView: 'chat'
        });
    }

    cancelStream = () => {
        if (this.abortController) {
            this.abortController.abort();
            this.abortController = null;
        }
    };

    private ensureWelcomeMessage = (messages: IChatMessage[]): IChatMessage[] => {
        // Check if first message is already the welcome message
        if (messages.length > 0 && messages[0].role === 'assistant' && messages[0].content === BEA_WELCOME_MESSAGE) {
            return messages;
        }

        // Prepend welcome message
        const welcomeMessage: IChatMessage = {
            role: 'assistant',
            content: BEA_WELCOME_MESSAGE,
            createdAt: messages.length > 0 ? messages[0].createdAt : new Date().toISOString()
        };
        return [welcomeMessage, ...messages];
    };

    private getCareMapKey = (): ICareMapKey | undefined => {
        const careMap = this.careMapState.getMostRecentCaremap();
        if (!careMap) {
            return undefined;
        }
        return {
            userId: careMap.userId,
            careMapId: careMap.careMapId
        };
    };

    loadChats = async () => {
        const entry = await this.apiClient.entry();
        if (!entry.links.chats) {
            return;
        }
        const chats = await this.chatClient.getChats(entry.links.chats.href);

        // strip citations from all messages
        const stripped = chats.map(chat => ({
            ...chat,
            messages: chat.messages.map(message => ({
                ...message,
                content: stripCitations(message.content, false)
            }))
        }));

        this.setState({ chats: stripped });
    };

    startNewConversation = () => {
        const welcomeMessage: IChatMessage = {
            role: 'assistant',
            content: BEA_WELCOME_MESSAGE,
            createdAt: new Date().toISOString()
        };

        this.setState({
            activeChat: null,
            messages: [welcomeMessage],
            currentView: 'chat'
        });
    };

    selectChat = (chatId: string) => {
        const chat = this.state.chats.find(c => c.chatId === chatId);
        if (chat) {
            // First update the messages while still on list view
            this.setState({
                activeChat: chat,
                messages: this.ensureWelcomeMessage(chat.messages)
            });
            // Delay allows React render and browser paint to complete before animating
            setTimeout(() => {
                this.setState({ currentView: 'chat' });
            }, 150);
        }
    };

    showConversationList = () => {
        this.setState({ currentView: 'list' });
    };

    showChat = () => {
        this.setState({ currentView: 'chat' });
    };

    sendMessage = async (text: string) => {
        const userMessage: IChatMessage = {
            role: 'user',
            content: text,
            createdAt: new Date().toISOString()
        };

        // Add user message to UI immediately
        const updatedMessages = [...this.state.messages, userMessage];
        this.setState({
            messages: updatedMessages,
            isSending: true,
            isStreaming: false,
            streamingText: ''
        });

        try {
            const entry = await this.apiClient.entry();
            const careMapKey = this.getCareMapKey();

            await this.sendMessageWithStreaming({
                streamUrl: entry.links.chatStream.href,
                message: text,
                chatId: this.state.activeChat?.chatId,
                careMapKey,
                updatedMessages
            });
        } catch (error) {
            console.error('Error sending message to Bea:', error);

            const errorMessage: IChatMessage = {
                role: 'assistant',
                content: 'I apologize, but I encountered an error. Please try again.',
                createdAt: new Date().toISOString()
            };

            this.setState({
                messages: [...updatedMessages, errorMessage],
                isSending: false,
                isStreaming: false,
                streamingText: ''
            });
        }
    };

    private sendMessageWithStreaming = async (params: {
        streamUrl: string;
        message: string;
        chatId?: string;
        careMapKey?: ICareMapKey;
        updatedMessages: IChatMessage[];
    }) => {
        const { streamUrl, message, chatId, careMapKey, updatedMessages } = params;

        let streamingText = '';
        let newChatId: string | undefined;

        // Cancel any existing stream before starting a new one
        this.cancelStream();
        this.abortController = new AbortController();

        this.setState({
            isSending: false,
            isStreaming: true,
            streamingText: ''
        });

        try {
            for await (const event of this.chatClient.streamChat({
                streamUrl,
                message,
                chatId,
                careMapKey,
                signal: this.abortController.signal
            })) {
            if (event.type === 'chat_created') {
                newChatId = event.chatId;
            } else if (event.type === 'text' && event.text) {
                streamingText += event.text;
                this.setState({ streamingText: stripCitations(streamingText, true) });
            } else if (event.type === 'done') {
                // Streaming complete - finalize the message
                const assistantMessage: IChatMessage = {
                    role: 'assistant',
                    content: stripCitations(streamingText, false) || 'I apologize, but I could not generate a response.',
                    createdAt: new Date().toISOString()
                };

                const finalMessages = [...updatedMessages, assistantMessage];
                const finalChatId = newChatId || chatId;

                // Create or update the chat in local state
                let updatedChat = this.state.activeChat;
                if (finalChatId) {
                    updatedChat = {
                        ...updatedChat,
                        chatId: finalChatId,
                        messages: finalMessages.filter(m => m.content !== BEA_WELCOME_MESSAGE),
                        lastMessageAt: new Date().toISOString()
                    } as IChat;
                }

                let updatedChats = this.state.chats;
                if (updatedChat) {
                    updatedChats = updatedChats.filter(c => c.chatId !== updatedChat!.chatId);
                    updatedChats = [updatedChat, ...updatedChats];
                }

                this.setState({
                    activeChat: updatedChat,
                    messages: this.ensureWelcomeMessage(finalMessages.filter(m => m.content !== BEA_WELCOME_MESSAGE)),
                    chats: updatedChats,
                    isStreaming: false,
                    streamingText: ''
                });
            } else if (event.type === 'error') {
                throw new Error(event.message || 'Stream error');
            }
        }
        } finally {
            this.abortController = null;
        }
    };

    getSummary = (chat: IChat): string => {
        // Get the most recent message
        if (chat.messages.length > 0) {
            return removeMarkdown(chat.messages[chat.messages.length - 1].content);
        }
        return 'New conversation';
    };

    hasChats = (): boolean => {
        return this.state.chats.length > 0;
    };
}
